/**
 * @file useOrderStageOptions.ts
 * @description Список стадий для фильтра «Стадия» реестра заказов
 * (phase19.31, SC49 — отбор по `stageId`).
 *
 * Стадии берём из воронок заказов магазина (`GET /admin/orders/pipelines`):
 * только активные, в порядке воронки. Если воронок несколько, к названию
 * стадии добавляется название воронки — иначе две «Новые» не различить.
 * Не загрузились — фильтр показывает только «Все стадии», реестр работает.
 */

import { useEffect, useState } from "react";

import { listOrderPipelines, type OrderPipelineWithStagesDto } from "../lib/api-orders";
import type { SelectFieldOption } from "../components/ui/SelectField";

/** Пункт «без отбора» — первым. */
export const ALL_STAGES_OPTION: SelectFieldOption = { value: "", label: "Все стадии" };

/** Воронки → пункты списка (чистая функция, покрыта тестом). */
export function buildOrderStageOptions(pipelines: OrderPipelineWithStagesDto[]): SelectFieldOption[] {
  const active = pipelines.filter((p) => p.isActive);
  const withPipeline = active.length > 1;
  const stages = active.flatMap((pipeline) =>
    [...pipeline.stages]
      .filter((stage) => stage.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((stage) => ({
        value: stage.id,
        label: withPipeline ? `${stage.name} · ${pipeline.name}` : stage.name,
      })),
  );
  return [ALL_STAGES_OPTION, ...stages];
}

export function useOrderStageOptions(): SelectFieldOption[] {
  const [options, setOptions] = useState<SelectFieldOption[]>([ALL_STAGES_OPTION]);

  useEffect(() => {
    let cancelled = false;
    listOrderPipelines()
      .then((pipelines) => {
        if (!cancelled) setOptions(buildOrderStageOptions(pipelines));
      })
      // Без стадий фильтр просто короче — реестр от этого не ломается.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return options;
}
